import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import PatientMarkersSection from "./PatientMarkersSection";
import PatientMedicationsSection from "./PatientMedicationsSection";
import PatientMetabolicTestsSection from "./PatientMetabolicTestsSection";
import PatientAnamnesisSection from "./PatientAnamnesisSection";
import PatientCoprologicalSection from "./PatientCoprologicalSection";
import ChatWidget from "@/components/ChatWidget";
import { ArrowLeft, FileText, Syringe, Calendar, Power, Pause, Mail, ScrollText, Home, ClipboardList, CheckCircle2, Loader2, AlertTriangle, Package, Download, Link2, Copy, X } from "lucide-react";
import { getStatusLabel } from "@/lib/statusLabels";

function Toggle({ enabled, onToggle, label, sublabel, testid, activeCls = "bg-success", inactiveCls = "bg-muted-foreground/30" }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-border last:border-0">
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{sublabel}</div>
      </div>
      <button
        type="button" role="switch" aria-checked={enabled}
        onClick={() => onToggle(!enabled)}
        className={`w-11 h-6 rounded-full transition-colors relative flex-none ${enabled ? activeCls : inactiveCls}`}
        data-testid={testid}
      >
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${enabled ? "left-5" : "left-0.5"}`} />
      </button>
    </div>
  );
}

function AvailableProtocolsList({ catalog, activeProtocolIds, activating, onActivate, testidPrefix = "available" }) {
  const activeSet = new Set(activeProtocolIds || []);
  return (
    <div className="max-h-64 overflow-y-auto space-y-1" data-testid={`${testidPrefix}-protocols-list`}>
      {catalog.length === 0 ? (
        <div className="text-xs text-muted-foreground py-2">Nenhum protocolo cadastrado no catálogo.</div>
      ) : catalog.map((proto) => {
        const isActive = activeSet.has(proto.id);
        const isBusy = activating === proto.id;
        return (
          <div
            key={proto.id}
            className={`flex items-center justify-between gap-2 py-2 px-2 rounded-sm ${isActive ? "bg-success/5 border border-success/20" : "hover:bg-muted"}`}
            data-testid={`${testidPrefix}-protocol-${proto.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold truncate">{proto.name}</div>
              <div className="text-[10px] text-muted-foreground">{proto.duration_days} dias{proto.category ? ` · ${proto.category}` : ""}</div>
            </div>
            {isActive ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-success uppercase tracking-wider flex-none" data-testid={`${testidPrefix}-already-active-${proto.id}`}>
                <CheckCircle2 className="w-3 h-3" /> Ativo
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onActivate(proto)}
                disabled={isBusy || !!activating}
                className="rounded-sm border border-primary/40 text-primary px-2.5 py-1 text-[11px] font-semibold hover:bg-primary/5 disabled:opacity-60 transition-colors flex-none inline-flex items-center gap-1"
                data-testid={`${testidPrefix}-activate-protocol-${proto.id}`}
              >
                {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                Adicionar
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

const AUDIT_LABELS = {
  "protocol.toggle": "Protocolo",
  "protocol.activate": "Ativou protocolo",
  "protocol.close": "Encerrou protocolo",
  "password.reset": "Redefiniu senha",
  "protocol_snapshot.edit": "Editou protocolo",
  "subscription.toggle": "Plano",
  "subscription.activate": "Ativou plano",
  "subscription.expired": "Plano venceu automaticamente",
  "email.toggle": "E-mail",
};

const PLAN_DURATIONS = [15, 30, 60, 90];

function PlanActivateModal({ patientId, current, onClose, onSaved }) {
  const [plan, setPlan] = useState(current?.plan || "premium");
  const [duration, setDuration] = useState(current?.duration_days || 30);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!plan.trim()) { toast.error("Informe o nome do plano."); return; }
    const d = Number(duration);
    if (!d || d < 1) { toast.error("Duração inválida."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/patients/${patientId}/subscription/activate`, {
        plan: plan.trim(), duration_days: d,
      });
      toast.success(data.replaced ? `Substituído: ${data.replaced} → ${data.plan}` : `Plano ${data.plan} ativado.`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao ativar plano.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose} data-testid="plan-activate-modal">
      <div className="bg-card border border-border rounded-sm p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="overline text-gold mb-1">Ativar plano</div>
        <h3 className="brand-wordmark uppercase text-xl font-bold text-primary mb-4">Nova assinatura</h3>

        <div className="space-y-4">
          <div>
            <label className="overline text-muted-foreground mb-1 block">Nome do plano</label>
            <input
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              placeholder="Ex: premium, essencial…"
              data-testid="plan-name-input"
            />
          </div>
          <div>
            <label className="overline text-muted-foreground mb-2 block">Duração</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {PLAN_DURATIONS.map((d) => (
                <button
                  key={d} type="button"
                  onClick={() => setDuration(d)}
                  className={`px-3 py-1.5 rounded-sm border text-xs font-semibold transition-colors ${Number(duration) === d ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"}`}
                  data-testid={`plan-duration-${d}`}
                >
                  {d} dias
                </button>
              ))}
            </div>
            <input
              type="number" min="1" value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              placeholder="ou digite um valor customizado"
              data-testid="plan-duration-custom"
            />
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <button onClick={onClose} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors" data-testid="cancel-plan-btn">Cancelar</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors" data-testid="save-plan-btn">
              {saving ? "Salvando…" : "Ativar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminPatientDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [activating, setActivating] = useState(null); // protocol_id being activated
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [resetPwdModal, setResetPwdModal] = useState(null); // { password }
  const [magicLink, setMagicLink] = useState(null); // { token, link, expires_at, message_template }

  const load = async () => {
    try {
      const [{ data }, cat] = await Promise.all([
        api.get(`/admin/patients/${id}`),
        api.get(`/admin/protocols`).catch(() => ({ data: [] })),
      ]);
      setData(data);
      setCatalog(cat.data || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar dados."); }
  };
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="text-sm text-muted-foreground overline">Carregando…</div>;

  const p = data.patient;
  const flags = data.flags || {};
  const activeProtocols = (data.active_protocols || []).filter((e) => e.status === "active");
  const activeProtocolIds = activeProtocols.map((e) => e.protocol_id);
  const series = (data.checkins || []).map((c) => ({ date: c.created_at.slice(5, 10), score: c.score, energy: c.energy, sleep: c.sleep, intestine: c.intestine, adherence: c.adherence }));

  const toggle = async (endpoint, enabled, key) => {
    setBusy(key);
    try {
      await api.post(`/admin/patients/${id}/${endpoint}/toggle`, { enabled });
      toast.success("Alteração aplicada.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao alterar.");
    } finally { setBusy(null); }
  };

  const activateProtocol = async (proto) => {
    // v3.33 Etapa 2: no confirmation — activate now attaches without replacing.
    setActivating(proto.id);
    try {
      const { data: res } = await api.post(`/admin/patients/${id}/protocol/activate`, { protocol_id: proto.id });
      toast.success(`Protocolo "${res.name}" adicionado.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao ativar protocolo.");
    } finally { setActivating(null); }
  };

  const closeProtocol = async (entry) => {
    const name = entry.snapshot_meta?.name || entry.protocol_id;
    if (!window.confirm(
      `Encerrar o protocolo "${name}" para este paciente?\n\n` +
      `Ao encerrar, o paciente deixa de ver este protocolo no painel. ` +
      `A rodada permanece no histórico do paciente e não pode ser reaberta — só reativada como uma nova rodada.\n\n` +
      `Deseja continuar?`
    )) return;
    setBusy(`close-${entry.id}`);
    try {
      await api.post(`/admin/patients/${id}/protocol/close`, { ap_id: entry.id });
      toast.success(`Protocolo "${name}" encerrado.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao encerrar protocolo.");
    } finally { setBusy(null); }
  };

  const resetPassword = async () => {
    if (!window.confirm(
      `Redefinir a senha deste paciente?\n\n` +
      `Uma nova senha será gerada. O paciente também receberá uma notificação automática no chat interno confirmando a alteração.\n\n` +
      `Deseja continuar?`
    )) return;
    setBusy("reset-password");
    try {
      const { data: res } = await api.post(`/admin/patients/${id}/reset-password`);
      setResetPwdModal({ password: res.generated_password });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao redefinir senha.");
    } finally { setBusy(null); }
  };


  return (
    <div className="space-y-8" data-testid="admin-patient-detail">
      <div>
        <Link to="/admin/pacientes" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-patients">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Link>
        <div className="mt-3 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline text-accent mb-1">Paciente</div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">{p.name}</h1>
            <p className="text-muted-foreground mt-1">{p.email}{p.phone ? ` · ${p.phone}` : ""}</p>
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="patient-sex-editor">
              <label className="text-xs text-muted-foreground">Sexo biológico:</label>
              <select
                value={p.sex || ""}
                onChange={async (e) => {
                  const newSex = e.target.value;
                  try {
                    await api.patch(`/admin/patients/${id}`, { sex: newSex });
                    toast.success(newSex ? "Sexo atualizado. Clique em 'Recalcular exames' para ajustar os alertas." : "Sexo removido.");
                    load();
                  } catch (err) { toast.error(formatApiError(err)); }
                }}
                className="text-xs px-2 py-1 rounded-sm border border-input bg-card"
                data-testid="patient-sex-select"
              >
                <option value="">— não informado</option>
                <option value="F">Feminino</option>
                <option value="M">Masculino</option>
              </select>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/admin/patients/${id}/markers/recalculate`);
                    toast.success(`Recalculado: ${data.updated} registro(s) atualizado(s).`);
                    load();
                  } catch (err) { toast.error(formatApiError(err)); }
                }}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm border border-primary text-primary hover:bg-primary/5"
                data-testid="recalculate-markers-btn"
                title="Recalcula os níveis de todos os exames deste paciente com a referência correta por sexo."
              >
                <Loader2 className="w-3 h-3" /> Recalcular exames
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/admin/patients/${id}/magic-link`);
                    setMagicLink(data);
                  } catch (err) { toast.error(formatApiError(err)); }
                }}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm border border-gold bg-gold/10 text-primary hover:bg-gold/20 font-semibold"
                data-testid="generate-magic-link-btn"
                title="Gera um link de acesso rápido para o paciente entrar sem senha. Válido por 7 dias, uso único."
              >
                <Link2 className="w-3 h-3" /> Gerar link de acesso
              </button>
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  const res = await api.get(`/admin/patients/${id}/consultation-pdf`, { responseType: "blob" });
                  const blob = new Blob([res.data], { type: "application/pdf" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `resumo-consulta-${(p.name || "paciente").toLowerCase().replace(/\s+/g, "-")}.pdf`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                  toast.success("PDF gerado.");
                } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar PDF."); }
              }}
              className="mt-3 inline-flex items-center gap-2 rounded-sm border border-primary text-primary px-3 py-2 text-xs font-semibold hover:bg-primary/5 transition-colors"
              data-testid="export-consultation-pdf-btn"
            >
              <Download className="w-3.5 h-3.5" /> Exportar PDF da consulta
            </button>
          </div>
          {(activeProtocols.length > 0 || catalog.length > 0) && (
            <div className="bg-card border border-border rounded-sm p-4 min-w-[320px] max-w-md" data-testid="protocol-side-card">
              <div className="overline text-muted-foreground mb-2">Protocolos ativos <span className="ml-1 text-[10px]">({activeProtocols.length})</span></div>
              {activeProtocols.length === 0 ? (
                <div className="text-xs text-muted-foreground py-1">Nenhum protocolo ativo — adicione um do catálogo abaixo.</div>
              ) : (
                <div className="space-y-2" data-testid="active-protocols-list">
                  {activeProtocols.map((entry) => {
                    const meta = entry.snapshot_meta || {};
                    const isBusy = busy === `close-${entry.id}`;
                    return (
                      <div key={entry.id}
                        className="border border-border rounded-sm p-3 space-y-2"
                        data-testid={`active-protocol-card-${entry.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-heading font-semibold text-sm truncate" data-testid={`active-protocol-name-${entry.id}`}>
                              {meta.name || entry.protocol_id}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                              Dia {entry.current_day ?? "—"} de {meta.duration_days ?? "—"}
                              {entry.archived ? " · arquivado no catálogo" : ""}
                            </div>
                          </div>
                          <span className={`inline-block overline px-2 py-0.5 rounded-sm border flex-none ${entry.enabled === false ? "border-muted-foreground/30 text-muted-foreground bg-muted" : "border-success/40 text-success bg-success/5"}`}>
                            {entry.enabled === false ? "Pausado" : "Ativo"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <Link
                            to={`/admin/pacientes/${id}/protocolo?ap=${entry.id}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                            data-testid={`edit-active-protocol-${entry.id}`}
                          >
                            Editar protocolo deste paciente →
                          </Link>
                          <button
                            type="button"
                            onClick={() => closeProtocol(entry)}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1 rounded-sm border border-destructive/40 text-destructive px-2.5 py-1 text-[11px] font-semibold hover:bg-destructive/5 disabled:opacity-60 transition-colors"
                            data-testid={`close-active-protocol-${entry.id}`}
                          >
                            {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                            Encerrar protocolo
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-border">
                <div className="overline text-gold mb-2 inline-flex items-center gap-1">
                  <ClipboardList className="w-3 h-3" /> Protocolos disponíveis
                </div>
                <AvailableProtocolsList
                  catalog={catalog}
                  activeProtocolIds={activeProtocolIds}
                  activating={activating}
                  onActivate={activateProtocol}
                  testidPrefix="side"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Controle clínico */}
      <div className="bg-card border border-border rounded-sm p-6" data-testid="patient-controls">
        <div className="overline text-gold mb-2">Controle clínico</div>
        <h3 className="brand-wordmark uppercase text-lg font-bold text-primary mb-1">Status do paciente</h3>
        <p className="text-xs text-muted-foreground mb-4">Ações abaixo são registradas em auditoria e refletem em minutos na visão do paciente.</p>
        <div className="divide-y divide-border">
          {data.protocol && (
            <Toggle
              enabled={flags.protocol_enabled}
              onToggle={(v) => toggle("protocol", v, "protocol")}
              label={<span className="inline-flex items-center gap-2"><Power className="w-4 h-4 text-primary" /> Protocolo ativo</span>}
              sublabel={flags.protocol_enabled ? `${data.protocol.name} — visível no dashboard do paciente` : "Protocolo pausado — não aparece no dashboard"}
              testid="toggle-protocol"
            />
          )}
          <div className="py-3">
            <div className="overline text-gold mb-2 inline-flex items-center gap-1">
              <ClipboardList className="w-3 h-3" /> Protocolos disponíveis no catálogo
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Atalho para ativar um protocolo padrão — para personalizar duração/itens, use &quot;Editar protocolo deste paciente&quot;.
            </p>
            <AvailableProtocolsList
              catalog={catalog}
              activeProtocolIds={activeProtocolIds}
              activating={activating}
              onActivate={activateProtocol}
              testidPrefix="status"
            />
          </div>
          <Toggle
            enabled={!flags.subscription_paused}
            onToggle={(v) => toggle("subscription", v, "subscription")}
            label={<span className="inline-flex items-center gap-2"><Pause className="w-4 h-4 text-primary" /> Plano ativo</span>}
            sublabel={flags.subscription_paused ? `Plano ${flags.subscription_plan || ""} pausado` : `Plano ${flags.subscription_plan || "—"} · status: ${flags.subscription_status}`}
            testid="toggle-subscription"
          />
          <div className="py-3 border-t border-border">
            <button
              type="button"
              onClick={resetPassword}
              disabled={busy === "reset-password"}
              className="w-full inline-flex items-center justify-center gap-2 rounded-sm border border-primary/40 text-primary px-3 py-2 text-xs font-semibold hover:bg-primary/5 disabled:opacity-60 transition-colors"
              data-testid="reset-patient-password-btn"
            >
              {busy === "reset-password" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              🔐 Redefinir senha do paciente
            </button>
            <div className="text-[10px] text-muted-foreground mt-1 text-center">
              Gera uma nova senha e avisa o paciente pelo chat interno automaticamente.
            </div>
          </div>
          <div className="py-3" data-testid="plan-card">
            {(() => {
              const sub = data.subscription || {};
              const remaining = flags.subscription_days_remaining;
              const status = flags.subscription_status;
              const expiring = flags.subscription_expiring_soon;
              const expired = status === "expired";
              const startDate = sub.started_at ? new Date(sub.started_at).toLocaleDateString("pt-BR") : "—";
              const expDate = sub.expires_at ? new Date(sub.expires_at).toLocaleDateString("pt-BR") : "—";
              const statusLabel = { active: "Ativo", expired: "Vencido", inactive: "Inativo" }[status] || status;
              const cardCls = expired
                ? "bg-destructive/5 border-destructive/40"
                : expiring
                ? "bg-amber-50 dark:bg-amber-900/10 border-amber-500/40"
                : "bg-muted/30 border-border";
              return (
                <div className={`rounded-sm border p-4 ${cardCls}`} data-testid="plan-details">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="overline text-gold mb-1 inline-flex items-center gap-1">
                        <Package className="w-3 h-3" /> Assinatura
                      </div>
                      <div className="font-heading font-semibold text-lg" data-testid="plan-name">
                        {sub.plan || "Sem plano ativo"}
                      </div>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <span
                          className={`inline-block overline px-2 py-0.5 rounded-sm border ${
                            expired ? "border-destructive/40 text-destructive bg-destructive/5"
                            : status === "active" ? "border-success/40 text-success bg-success/5"
                            : "border-muted-foreground/30 text-muted-foreground bg-muted"
                          }`}
                          data-testid="plan-status"
                        >
                          {statusLabel}
                        </span>
                        {expiring && !expired && (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500 font-semibold" data-testid="plan-expiring-badge">
                            <AlertTriangle className="w-3 h-3" /> Vence em {remaining} dia{remaining !== 1 ? "s" : ""}
                          </span>
                        )}
                        {expired && (
                          <span className="inline-flex items-center gap-1 text-xs text-destructive font-semibold" data-testid="plan-expired-badge">
                            <AlertTriangle className="w-3 h-3" /> Plano vencido
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button" onClick={() => setShowPlanModal(true)}
                      className="rounded-sm border border-primary/40 text-primary px-3 py-1.5 text-xs font-semibold hover:bg-primary/5 transition-colors flex-none"
                      data-testid="activate-plan-btn"
                    >
                      {sub.plan ? "Trocar plano" : "Ativar plano"}
                    </button>
                  </div>

                  {sub.plan && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
                      <div>
                        <div className="overline text-muted-foreground">Duração</div>
                        <div className="font-semibold" data-testid="plan-duration">{sub.duration_days ? `${sub.duration_days} dias` : "—"}</div>
                      </div>
                      <div>
                        <div className="overline text-muted-foreground">Início</div>
                        <div className="font-semibold" data-testid="plan-started">{startDate}</div>
                      </div>
                      <div>
                        <div className="overline text-muted-foreground">Vencimento</div>
                        <div className="font-semibold" data-testid="plan-expires">{expDate}</div>
                      </div>
                      <div>
                        <div className="overline text-muted-foreground">Restam</div>
                        <div className={`font-semibold ${expired ? "text-destructive" : expiring ? "text-amber-600" : ""}`} data-testid="plan-remaining">
                          {remaining === null || remaining === undefined ? "—"
                            : expired ? `${Math.abs(remaining)} dias atrás`
                            : `${remaining} dia${remaining !== 1 ? "s" : ""}`}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {(data.subscription_history || []).length > 0 && (
              <details className="mt-3" data-testid="plan-history">
                <summary className="cursor-pointer text-xs text-muted-foreground hover:text-primary">
                  Ver histórico de planos ({data.subscription_history.length})
                </summary>
                <ul className="mt-2 space-y-1">
                  {data.subscription_history.slice().reverse().map((h, i) => (
                    <li key={i} className="text-xs py-2 px-3 rounded-sm bg-muted/30 flex items-center justify-between gap-2">
                      <span>
                        <span className="font-semibold">{h.plan}</span>
                        {h.duration_days ? ` · ${h.duration_days} dias` : ""}
                      </span>
                      <span className="text-muted-foreground">
                        {h.started_at ? new Date(h.started_at).toLocaleDateString("pt-BR") : ""} →
                        {h.ended_at ? new Date(h.ended_at).toLocaleDateString("pt-BR") : ""} · {getStatusLabel("protocol_history", h.status).label}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
          <Toggle
            enabled={flags.email_enabled}
            onToggle={(v) => toggle("email", v, "email")}
            label={<span className="inline-flex items-center gap-2"><Mail className="w-4 h-4 text-primary" /> Comunicações por e-mail</span>}
            sublabel={flags.email_enabled ? "Paciente recebe e-mails transacionais" : "E-mails pausados (nenhuma comunicação será enviada)"}
            testid="toggle-email"
          />
        </div>
      </div>

      {/* Patient medications (Etapa 2 — Fármacos) */}
      <PatientMedicationsSection patientId={id} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-sm p-6 lg:col-span-2">
          <div className="overline text-muted-foreground mb-1">Evolução clínica</div>
          <h3 className="font-heading font-semibold mb-4">Check-ins semanais ({data.checkins.length})</h3>
          <div className="h-64">
            {series.length > 0 ? (
              <ResponsiveContainer>
                <LineChart data={series}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis domain={[0, 10]} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
                  <Line type="monotone" dataKey="energy" name="Energia" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="sleep" name="Sono" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="intestine" name="Intestino" stroke="hsl(var(--chart-3))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="adherence" name="Adesão" stroke="hsl(var(--chart-4))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">Sem check-ins registrados.</div>
            )}
          </div>
        </div>

        <div data-testid="patient-chat-container">
          <ChatWidget otherUserId={id} title={p.name} />
        </div>
      </div>

      {/* Audit log */}
      <div className="bg-card border border-border rounded-sm p-6" data-testid="audit-log">
        <div className="overline text-muted-foreground mb-2 inline-flex items-center gap-2"><ScrollText className="w-4 h-4" /> Auditoria</div>
        <h3 className="font-heading font-semibold mb-3">Histórico de ações administrativas</h3>
        {(data.audit_log || []).length === 0 ? (
          <div className="text-sm text-muted-foreground">Nenhuma ação registrada ainda.</div>
        ) : (
          <ul className="space-y-2">
            {data.audit_log.map((a) => (
              <li key={a.id} className="flex items-start gap-3 text-sm py-2 border-b border-border last:border-0" data-testid={`audit-row-${a.id}`}>
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-none" />
                <div className="flex-1">
                  <div><b>{AUDIT_LABELS[a.action] || a.action}</b> — {String(a.before)} → <span className="text-primary font-semibold">{String(a.after)}</span></div>
                  <div className="text-xs text-muted-foreground">{a.admin_name} · {new Date(a.created_at).toLocaleString("pt-BR")}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Recent exams */}
      <div>
        <div className="overline text-muted-foreground mb-3">Exames laboratoriais</div>
        {data.exams.length === 0 ? (
          <div className="bg-card border border-border rounded-sm p-6 text-sm text-muted-foreground">Sem exames.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.exams.map((ex) => (
              <Link key={ex.id} to={`/admin/exames?highlight=${ex.id}`} className="bg-card border border-border rounded-sm p-4 hover:border-primary transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-sm">{ex.exam_type}</span>
                  </div>
                  <span className="overline text-muted-foreground">{getStatusLabel("exam", ex.status).label}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-2">{new Date(ex.created_at).toLocaleDateString("pt-BR")}</div>
                {ex.observations && <div className="text-xs mt-2 p-2 bg-muted rounded-sm">{ex.observations}</div>}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Patient markers section */}
      <PatientMarkersSection
        patientId={id}
        protocolsHistory={[
          ...(data.protocol ? [{ id: data.protocol.id, name: data.protocol.name }] : []),
          ...((data.patient?.protocol_history || []).map((h) => ({
            id: (h.snapshot && h.snapshot.id) || h.protocol_id,
            name: (h.snapshot && h.snapshot.name) || h.protocol_id,
          }))),
        ].filter((p) => p.id)}
      />

      {/* Metabolic tests linked to this patient */}
      <PatientMetabolicTestsSection
        patientId={id}
        patientName={p.name}
        patientEmail={p.email}
      />

      {/* Anamnesis forms linked to this patient */}
      <PatientAnamnesisSection
        patientId={id}
        patientName={p.name}
        patientEmail={p.email}
      />

      {/* Prova Coprológica Funcional — qualitative exam with deterministic rules */}
      <PatientCoprologicalSection patientId={id} />

      {/* Applications & appointments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="overline text-muted-foreground mb-3">Aplicações ({data.applications.length})</div>
          <div className="space-y-2">
            {data.applications.slice(0, 5).map((a) => (
              <div key={a.id} className="bg-card border border-border rounded-sm p-3 flex items-center gap-3 text-sm">
                <Syringe className="w-4 h-4 text-accent" />
                <div className="flex-1">
                  <div className="font-medium">{a.substance} · {a.dose}</div>
                  <div className="text-xs text-muted-foreground">{new Date(a.scheduled_at).toLocaleString("pt-BR")}</div>
                </div>
                <span className={`overline px-2 py-0.5 rounded-sm border ${getStatusLabel("application", a.status).cls}`}>{getStatusLabel("application", a.status).label}</span>
              </div>
            ))}
            {data.applications.length === 0 && <div className="text-sm text-muted-foreground">Sem aplicações.</div>}
          </div>
        </div>
        <div>
          <div className="overline text-muted-foreground mb-3">Consultas ({data.appointments.length})</div>
          <div className="space-y-2">
            {data.appointments.slice(0, 5).map((a) => (
              <div key={a.id} className="bg-card border border-border rounded-sm p-3 flex items-start gap-3 text-sm">
                <Calendar className="w-4 h-4 text-primary mt-0.5 flex-none" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium capitalize">{a.service_name || a.appointment_type}</div>
                  <div className="text-xs text-muted-foreground">{new Date(a.scheduled_at).toLocaleString("pt-BR")}</div>
                  {a.appointment_type === "domiciliar" && a.address && (
                    <div className="text-[11px] text-muted-foreground mt-1 inline-flex gap-1 items-start">
                      <Home className="w-3 h-3 mt-0.5 flex-none" />
                      <span>{a.address.street}, {a.address.number}{a.address.complement ? ` — ${a.address.complement}` : ""} · {a.address.neighborhood} · {a.address.city} · {a.address.zip}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {data.appointments.length === 0 && <div className="text-sm text-muted-foreground">Sem consultas.</div>}
          </div>
        </div>
      </div>
      {busy && <div className="fixed bottom-4 right-4 bg-card border border-border rounded-sm px-3 py-2 text-xs shadow-lg">Aplicando…</div>}
      {showPlanModal && (
        <PlanActivateModal
          patientId={id}
          current={data.subscription}
          onClose={() => setShowPlanModal(false)}
          onSaved={async () => { setShowPlanModal(false); await load(); }}
        />
      )}
      {resetPwdModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4"
          onClick={() => setResetPwdModal(null)}
          data-testid="reset-password-modal"
        >
          <div
            className="bg-card border border-border rounded-sm p-6 max-w-md w-full space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <div className="overline text-gold mb-1">🔐 Senha redefinida</div>
              <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">Nova senha do paciente</h3>
              <p className="text-xs text-muted-foreground mt-2">
                Guarde ou envie esta senha para o paciente pelo WhatsApp externo. Uma mensagem automática foi enviada pelo chat interno confirmando a redefinição.
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-sm border border-border bg-muted/50 p-3">
              <div className="text-lg font-mono flex-1 tracking-wider" data-testid="reset-password-value">
                {resetPwdModal.password}
              </div>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(resetPwdModal.password); toast.success("Senha copiada."); }}
                className="rounded-sm border border-primary/40 text-primary px-3 py-1.5 text-xs font-semibold hover:bg-primary/5 transition-colors"
                data-testid="copy-reset-password-btn"
              >
                Copiar
              </button>
            </div>
            <button
              type="button"
              onClick={() => setResetPwdModal(null)}
              className="w-full rounded-sm bg-primary text-primary-foreground py-2 text-sm font-semibold"
              data-testid="close-reset-password-modal-btn"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {magicLink && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="magic-link-modal">
          <div className="bg-card border border-border rounded-sm p-6 w-full max-w-lg">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline text-gold mb-1 inline-flex items-center gap-2">
                  <Link2 className="w-3.5 h-3.5" /> Acesso rápido
                </div>
                <h2 className="brand-wordmark uppercase text-lg font-bold text-primary">Link de acesso gerado</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Válido por {magicLink.ttl_days} dias · Uso único · Links anteriores foram invalidados
                </p>
              </div>
              <button
                onClick={() => setMagicLink(null)}
                className="p-1 hover:bg-muted rounded-sm"
                data-testid="close-magic-link-modal-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mb-4">
              <label className="overline text-muted-foreground mb-1 block">Link</label>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={magicLink.link}
                  className="flex-1 px-3 py-2 rounded-sm border border-input bg-muted/40 text-xs font-mono"
                  data-testid="magic-link-url"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(magicLink.link);
                    toast.success("Link copiado.");
                  }}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-sm border border-primary text-primary hover:bg-primary/5 text-xs font-semibold"
                  data-testid="magic-link-copy-url"
                >
                  <Copy className="w-3.5 h-3.5" /> Copiar
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="overline text-muted-foreground mb-1 block">Mensagem pronta para WhatsApp</label>
              <textarea
                readOnly
                rows={3}
                value={magicLink.message_template}
                className="w-full px-3 py-2 rounded-sm border border-input bg-muted/40 text-xs leading-relaxed"
                data-testid="magic-link-message"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(magicLink.message_template);
                  toast.success("Mensagem copiada.");
                }}
                className="mt-2 inline-flex items-center gap-1 px-3 py-2 rounded-sm border border-primary text-primary hover:bg-primary/5 text-xs font-semibold"
                data-testid="magic-link-copy-message"
              >
                <Copy className="w-3.5 h-3.5" /> Copiar mensagem
              </button>
            </div>

            <p className="text-[11px] text-muted-foreground italic mb-4">
              Expira em {new Date(magicLink.expires_at).toLocaleString("pt-BR")}.
              Se você gerar outro link para este paciente, este será invalidado automaticamente.
            </p>

            <button
              onClick={() => setMagicLink(null)}
              className="w-full rounded-sm bg-primary text-primary-foreground py-2 text-sm font-semibold"
              data-testid="magic-link-done-btn"
            >
              Pronto
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
